import { color, Icon } from '@primer-io/goat'
import * as ArticleComponents from 'components/ArticleComponents'
import IconCarousel from 'components/IconCarousel'
import { FullWidthGuide, GuideLink, Header, OverviewSection, OverviewTopSection } from 'components/OverviewComponents'
import Tabs, { Tab } from 'components/Tabs'
import Grid from 'ds/Grid'
import React from 'react'
import styled from 'styled-components'
import breakpoints from '../../../theme/breakpoints'
import BestPracticesImg from './assets/best-practices.png'
import CoverImg from './assets/cover.png'
import CreateWorkflowImg from './assets/create-workflow.png'
import MonitorWorkflowsImg from './assets/monitoring.png'
import PaymentAppImg from './assets/payment-app.png'
import { useCases, workflowApps } from './data'

///////////////////////////////////////////
// Tabs
///////////////////////////////////////////

const TabTitleWithIcon = ({ icon, title }) => (
	<div style={{ textAlign: 'left' }} id={title}>
		<Icon as={icon} /> <br />
		{title}
	</div>
)

// tuple [width, height]
const imgSizes = {
	large: [768, 332],
	small: [288, 224],
}

const TabContentImgContainer = styled.div`
	align-items: center;
	background-color: ${color('gray-50')};
	border-radius: 4px;
	display: flex;
	overflow: hidden;
	place-content: center;

	:first-child {
		margin-top: 24px;
	}
`

const TabContentImg = styled.img`
	aspect-ratio: ${imgSizes.small.join(' / ')};
	object-fit: contain;

	${p => p.theme.above('medium')`
	  aspect-ratio: ${imgSizes.large.join(' / ')};
	`}
`

///////////////////////////////////////////
// Root
///////////////////////////////////////////

export default function AutomationOverview() {
	return (
		<ArticleComponents.Wrapper>
			<OverviewSection>
				<OverviewTopSection
					title='Automation'
					description={
						<>
							<ArticleComponents.P>
								Workflows enable you to build end-to-end payment and commerce automation without writing any code at
								all.
							</ArticleComponents.P>
						</>
					}
					imgSrc={CoverImg}
				/>
			</OverviewSection>

			<OverviewSection>
				<FullWidthGuide
					title='Create a workflow'
					description='Primer gives you all the building blocks to create & manage your workflows based on your own business
				logic and requirements.'
					imgBackground={color('indigo-100')}
					imgSrc={CreateWorkflowImg}
					buttonContent='Get started'
					to='./create-workflow'
				/>
			</OverviewSection>
			<OverviewSection>
				<Header>
					Use Workflows to drive value
					<br /> across your business
				</Header>

				<Tabs variant='underline'>
					{useCases.map(({ title, icon, imgSrc, imgSrcSmall }) => (
						<Tab key={title} title={<TabTitleWithIcon icon={icon} title={title} />} id={title}>
							<TabContentImgContainer>
								<TabContentImg
									alt={title}
									sizes={`(max-width: ${breakpoints.medium.width}px) 100vw, 1000px`}
									src={imgSrc}
									srcSet={`${imgSrcSmall} ${breakpoints.medium.width}w, ${imgSrc}`}
								/>
							</TabContentImgContainer>
						</Tab>
					))}
				</Tabs>
			</OverviewSection>
			<OverviewSection>
				<Header>
					Power your flows with <br />
					the apps you need
				</Header>
				<div style={{ overflow: 'hidden' }}>
					<IconCarousel icons={workflowApps} />
				</div>
			</OverviewSection>
			<OverviewSection>
				<FullWidthGuide
					title='Payments App'
					description='Workflows includes Primer’s native Payments app which allows you to bring the power of Workflows to your payment infrastructure.'
					imgSrc={PaymentAppImg}
					imgBackground={color('yellow-200')}
					buttonContent='Learn more'
					to='./payments-app'
					reverse
				/>
			</OverviewSection>
			<OverviewSection>
				<Header>Build the best workflows</Header>
				<Grid cols={{ md: 2, lg: 2, xl: 2 }} gap={24} style={{ marginTop: 24 }}>
					<GuideLink
						title='Best practices'
						description='Tips to make your workflows more resilient, more efficient, and easier to maintain.'
						link='./best-practices'
						imgSrc={BestPracticesImg}
					/>
					<GuideLink
						title='Easily monitor your workflows'
						description='We provide the visibility needed to understand & debug every Workflow Run.'
						link='./monitor-workflows'
						imgSrc={MonitorWorkflowsImg}
					/>
				</Grid>
			</OverviewSection>
		</ArticleComponents.Wrapper>
	)
}
